import { TeamOutlined, UserOutlined } from "@ant-design/icons";

import { RVIcon, RVIconSizeOptions } from "@regal-voice/ui";
import { AppPagePaths } from "App/routes/paths";
import { AIAgentPaths } from "Pages/agentBuilder/Pages/paths";
import { orchestrationPathResources } from "Pages/orchestration/Pages/orchestrationPaths";

import { SpotlightRoutingItem } from "./Components/Routing/SpotlightRoutingItem";
import { SpotlightOption } from "./types";

type DefaultResultsAvailability = {
    showAgentDesktop: boolean;
    showReporting: boolean;
    showSettings: boolean;
    showAIAgents: boolean;
    showCustomAgents: boolean;
    showKnowledgeBase: boolean;
    showContacts: boolean;
    showCampaigns: boolean;
    showJourneys: boolean;
    showUsers: boolean;
};

export function getDefaultResults(
    navigate: (path: string) => void,
    onClose: () => void,
    availability: DefaultResultsAvailability
): SpotlightOption[] {
    const routingOptions: SpotlightOption["options"] = [];

    if (availability.showAgentDesktop) {
        routingOptions.push({
            label: (
                <SpotlightRoutingItem
                    name="Agent Desktop"
                    description="Main workspace for human agents"
                    searchValue=""
                    route={AppPagePaths.agentDesktop}
                    onCallback={() => {
                        navigate(AppPagePaths.agentDesktop);
                        onClose();
                    }}
                />
            ),
            value: AppPagePaths.agentDesktop,
        });
    }

    if (availability.showReporting) {
        routingOptions.push({
            label: (
                <SpotlightRoutingItem
                    name="Reporting"
                    description="Reporting and analytics dashboards"
                    searchValue=""
                    route={AppPagePaths.reporting}
                    onCallback={() => {
                        navigate(AppPagePaths.reporting);
                        onClose();
                    }}
                />
            ),
            value: AppPagePaths.reporting,
        });
    }

    if (availability.showSettings) {
        routingOptions.push({
            label: (
                <SpotlightRoutingItem
                    name="Settings"
                    description="Configure your workspace"
                    searchValue=""
                    route={AppPagePaths.settings}
                    onCallback={() => {
                        navigate(AppPagePaths.settings);
                        onClose();
                    }}
                />
            ),
            value: AppPagePaths.settings,
        });
    }

    const aiAgentsOptions: SpotlightOption["options"] = [];

    if (availability.showAIAgents) {
        aiAgentsOptions.push({
            label: (
                <SpotlightRoutingItem
                    name="Agent Templates"
                    description="Browse and manage AI agent templates"
                    searchValue=""
                    route={`${AppPagePaths.aiAgents}/${AIAgentPaths.templates}`}
                    onCallback={() => {
                        navigate(`${AppPagePaths.aiAgents}/${AIAgentPaths.templates}`);
                        onClose();
                    }}
                />
            ),
            value: "ai-agent-templates",
        });
    }

    if (availability.showCustomAgents) {
        aiAgentsOptions.push({
            label: (
                <SpotlightRoutingItem
                    name="Create new AI agent"
                    description="Type /create ai agent"
                    searchValue=""
                    route={`${AppPagePaths.aiAgents}/${AIAgentPaths.agentNew}`}
                    onCallback={() => {
                        navigate(`${AppPagePaths.aiAgents}/${AIAgentPaths.agentNew}`);
                        onClose();
                    }}
                />
            ),
            value: "create-ai-agent",
        });
    }

    if (availability.showKnowledgeBase) {
        aiAgentsOptions.push({
            label: (
                <SpotlightRoutingItem
                    name="Knowledge Bases"
                    description="Browse and manage knowledge bases"
                    searchValue=""
                    route={`${AppPagePaths.aiAgents}/${AIAgentPaths.knowledgeBase}`}
                    onCallback={() => {
                        navigate(`${AppPagePaths.aiAgents}/${AIAgentPaths.knowledgeBase}`);
                        onClose();
                    }}
                />
            ),
            value: "knowledge-bases",
        });
    }

    // display this at the bottom
    if (availability.showAIAgents) {
        aiAgentsOptions.push({
            label: (
                <SpotlightRoutingItem
                    icon={<RVIcon icon={"ai-agents"} size={RVIconSizeOptions.SMALL} />}
                    name="Search for AI Agents by name"
                    description="Start typing to search"
                    searchValue=""
                    route=""
                    onCallback={() => {
                        // Placeholder
                    }}
                />
            ),
            value: "search-ai-agents",
        });
    }

    const audienceOptions: SpotlightOption["options"] = availability.showContacts
        ? [
              {
                  label: (
                      <SpotlightRoutingItem
                          icon={<TeamOutlined />}
                          name="Search for contacts"
                          description="Search by name, phone, or external ID"
                          searchValue=""
                          route=""
                          onCallback={() => {
                              // Placeholder
                          }}
                      />
                  ),
                  value: "search-contacts",
              },
          ]
        : [];

    const campaignsOptions: SpotlightOption["options"] = availability.showCampaigns
        ? [
              {
                  label: (
                      <SpotlightRoutingItem
                          name="Create new campaign"
                          description="Type /create campaign"
                          searchValue=""
                          route={`${AppPagePaths.orchestration}/${orchestrationPathResources.campaigns}/new`}
                          onCallback={() => {
                              navigate(`${AppPagePaths.orchestration}/${orchestrationPathResources.campaigns}/new`);
                              onClose();
                          }}
                      />
                  ),
                  value: "create-campaign",
              },
              {
                  label: (
                      <SpotlightRoutingItem
                          icon={<RVIcon icon={"campaign"} size={RVIconSizeOptions.SMALL} />}
                          name="Search for campaigns by name"
                          description="Start typing to search"
                          searchValue=""
                          route=""
                          onCallback={() => {
                              // Placeholder
                          }}
                      />
                  ),
                  value: "search-campaigns",
              },
          ]
        : [];

    const journeysOptions: SpotlightOption["options"] = [];

    if (availability.showJourneys) {
        journeysOptions.push(
            {
                label: (
                    <SpotlightRoutingItem
                        name="Create new journey"
                        description="Type /create journey"
                        searchValue=""
                        route={`${AppPagePaths.orchestration}/${orchestrationPathResources.journeys}/new`}
                        onCallback={() => {
                            navigate(`${AppPagePaths.orchestration}/${orchestrationPathResources.journeys}/new`);
                            onClose();
                        }}
                    />
                ),
                value: "create-journey",
            },
            {
                label: (
                    <SpotlightRoutingItem
                        icon={<RVIcon icon={"journey"} size={RVIconSizeOptions.SMALL} />}
                        name="Search for journeys by name"
                        description="Start typing to search"
                        searchValue=""
                        route=""
                        onCallback={() => {
                            // Placeholder
                        }}
                    />
                ),
                value: "search-journeys",
            }
        );
    }

    const usersOptions: SpotlightOption["options"] = availability.showUsers
        ? [
              {
                  label: (
                      <SpotlightRoutingItem
                          icon={<UserOutlined />}
                          name="Search for users by name"
                          description="Start typing to search"
                          searchValue=""
                          route=""
                          onCallback={() => {
                              // Placeholder
                          }}
                      />
                  ),
                  value: "search-users",
              },
          ]
        : [];

    return [
        {
            label: "Routing",
            options: routingOptions,
        },
        {
            label: "AI Agents",
            options: aiAgentsOptions,
        },
        {
            label: "Audience",
            options: audienceOptions,
        },
        {
            label: "Campaigns",
            options: campaignsOptions,
        },
        {
            label: "Journeys",
            options: journeysOptions,
        },
        {
            label: "Users",
            options: usersOptions,
        },
    ].filter((section) => section.options.length > 0);
}
